import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

/** The small set of pieces every screen here is built from. */

export function Button({ variant = 'secondary', loading, className, children, disabled, ...rest }:
ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost'; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex h-9 items-center justify-center gap-2 rounded-btn px-3.5 text-sm font-medium transition-colors disabled:opacity-45',
        variant === 'primary' && 'bg-ink text-on-ink hover:opacity-90',
        variant === 'secondary' && 'border border-bd bg-bg2 text-t1 hover:border-bd2',
        variant === 'ghost' && 'text-t2 hover:bg-hover hover:text-t1',
        className,
      )}
    >
      {loading && <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  );
}

export const Input = ({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) => (
  <input {...rest} className={clsx('h-9 w-full rounded-btn border border-bd bg-bg2 px-3 text-sm outline-none placeholder:text-t3 focus:border-bd2', className)} />
);

export const Select = ({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...rest} className={clsx('h-9 rounded-btn border border-bd bg-bg2 px-2.5 text-sm outline-none focus:border-bd2', className)}>{children}</select>
);

export const Card = ({ className, children, pad = true }: { className?: string; children: ReactNode; pad?: boolean }) => (
  <div className={clsx('rounded-card border border-bd bg-bg2 shadow-card', pad && 'p-5', className)}>{children}</div>
);

export const Field = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-t2">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-t3">{hint}</span>}
  </label>
);

const TONES = {
  critical: 'bg-err-bg text-err',
  high: 'bg-warn-bg text-warn',
  medium: 'bg-bg3 text-t2',
  low: 'bg-bg3 text-t3',
  none: 'bg-ok-bg text-ok',
  neutral: 'bg-bg3 text-t2',
} as const;

export const Badge = ({ tone = 'neutral', children }: { tone?: keyof typeof TONES; children: ReactNode }) => (
  <span className={clsx('inline-flex items-center rounded-chip px-2 py-0.5 text-xs font-medium', TONES[tone])}>{children}</span>
);

export const Loading = ({ label = 'Loading' }: { label?: string }) => (
  <div className="flex items-center gap-2 py-10 text-sm text-t3">
    <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />{label}…
  </div>
);

export const ErrorNote = ({ error }: { error: unknown }) => {
  if (!error) return null;
  return (
    <div className="rounded-btn border border-[color-mix(in_srgb,var(--err)_35%,transparent)] bg-err-bg px-3 py-2 text-sm text-err">
      {(error as Error).message ?? 'Something went wrong.'}
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) => (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
    <div>
      <h1 className="text-2xl font-bold tracking-[-0.02em]">{title}</h1>
      {subtitle && <p className="mt-0.5 max-w-2xl text-sm text-t2">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);
